/**
 * モバイルのソフトキーボード対応 (2026-08-17)。
 *
 * 課題:
 *   position:fixed のパネル（Sheet）は iOS Safari / Android Chrome で
 *   ソフトキーボードが出ても縮まないため、下端の入力欄が画面外に隠れて
 *   タップも入力もできなくなる（掲示板スレッドの実害）。
 *
 * 方針:
 *   window.visualViewport（= 実際に見えている領域）を購読し、
 *   その height / offsetTop をパネルへ inline style で当てる。
 *   - モバイル幅のときだけ有効（デスクトップのピンチズームで暴れないように）
 *   - visualViewport 非対応環境では null を返し、呼び出し側は既存の CSS のまま
 */
import { useEffect, useState } from 'react';

import { useIsMobile } from './useIsMobile';

export type VisualViewportRect = { height: number; offsetTop: number };

export function useVisualViewport(active: boolean): VisualViewportRect | null {
  const isMobile = useIsMobile();
  const [rect, setRect] = useState<VisualViewportRect | null>(null);

  useEffect(() => {
    const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!active || !isMobile || !vv) {
      setRect(null);
      return;
    }
    const read = () => setRect({ height: vv.height, offsetTop: vv.offsetTop });
    read();
    vv.addEventListener('resize', read);
    vv.addEventListener('scroll', read);
    return () => {
      vv.removeEventListener('resize', read);
      vv.removeEventListener('scroll', read);
    };
  }, [active, isMobile]);

  return rect;
}
